import type { icModel, infrastructurePortalUtil } from 'generated-ts-common';
import type { ValidIconName } from '../components';

type HealthCareProvider = icModel.saf.infrastructure.model.hpd.HealthCareProvider;
type GenCode = infrastructurePortalUtil.saf.infrastructure.model.svs.GenCode;

type ID = icModel.saf.infrastructure.model.common.ID;
type QuestionnaireFormInstance = icModel.saf.infrastructure.model.form.QuestionnaireFormInstance;

export interface RecordImportEventParam {
  documentDataAccessorKey: string;
  uniqueDocID: ID;
  renderOptions?: {
    renderTo?: string;
  };
}

export interface BatchRecordImportEventParam {
  documentsListDataAccessorKey: string;
  importAll: boolean;
}

export interface DocumentTitleParams {
  /** Title and subtitle(content). */
  desktop: string[];
  /** Title and subtitle(content). */
  mobile: string[];
  /** Icon for panel header */
  icon: ValidIconName;
}

export interface DisplayDocumentEventParam {
  documentDataAccessorKey: string;
  formatCode?: icModel.saf.infrastructure.model.svs.GenCode;
  mimeType: string;
  setLoading?: (showLoadmask: boolean) => void;
  renderOptions?: DocumentRenderOptionsParam;
  title?: DocumentTitleParams;
}

export interface FloatingToolbarRenderParam {
  /**
   * Hides whole main component.
   */
  hide?: boolean;
  /**
   * Hides 3 dots tool menu with downloads and import buttons.
   */
  hide3DotsMenu?: boolean;
  /**
   * Hides attachment tool menu.
   */
  hideAttachmentMenu?: boolean;
}

export interface DocumentRenderOptionsParam {
  renderTo?: string;
  renderHeader?: boolean;
  renderFirstHeader?: boolean;
  renderToContainer?: boolean;
  forbiddenDownloadOnRetrieve?: boolean;
  hideCollapseTool?: boolean;
  closable?: boolean;
  maximized?: boolean;
  floatingToolbar?: FloatingToolbarRenderParam;
}

export type SetPatientFilterEventParam = {
  firstName?: string;
  lastName?: string;
  gender?: string;
  id?: string;
  institution?: string;
  ssn?: string;
  dateOfBirth?: string;
  localPatientsOnly?: boolean;
  zpiSearch?: boolean;
  empty?: boolean;
};

export type DocRemovedTeleconsultationEventParam = {
  mappedTitle: string;
  mappedContent: string;
  mappedTitleMobile: string;
  mappedContentMobile: string;
  document: icModel.saf.infrastructure.model.document.DocumentInformation;
  icon: string;
  editable: boolean;
  editableMetadata: boolean;
};

export type DocumentQueryResultEventParam = {
  successfullySubmittedDocumentId: string;
  /** Id of imported document from DataAccessor. */
  successfullySubmittedImportId: string;
  documentTitle: string;
  errorCode?: string;
};

export type RefreshDocumentQueryResultEventParam = {
  /** Whether to immediately trigger a forward, preselecting the uploaded documents. */
  enableForward?: boolean;
  documents: DocumentQueryResultEventParam[];
};

export type HCProviderSelectedEventParam = {
  provider: {
    data: HealthCareProvider;
    id: string;
  };
  maskedCmp: unknown;
};

export type RIPImportStateEventParam = {
  importResult: {
    currentImportID: string;
    documentID: string;
    documentTitle: string;
    outcomeExpected: boolean;
    patientName: string;
    progress: number;
    startDateFormatted: string;
    state: string;
  };
  importRequestOriginEvent: string;
};

export type CreateEditStartEventParam = {
  actionsToShow?: Array<'save' | 'cancel'>;
};

export type CreateStartEventParam = CreateEditStartEventParam & {
  // optionally, patient data can be passed to pre-fill the view
  patient?: icModel.saf.infrastructure.model.patient.EnhancedPatient;
};

export type EditStartEventParam = CreateEditStartEventParam & {
  patient: icModel.saf.infrastructure.model.patient.EnhancedPatient | undefined;
};
export type PatientWizardSelectionParam = icModel.saf.infrastructure.model.patient.EnhancedPatient;

export type OpenHealthcareProviderDetailsParam = icModel.saf.infrastructure.model.hpd.CustomHealthCareProvider;

/**
 * Represents parameters for retrieving a Questionnaire or a QuestionnaireResponse.
 *
 * NOTE: Only provide either the questionnaireId OR the questionnaireResponseId but not both.
 */
export type QuestionnaireParam = {
  /** Id of the Questionnaire from FHIR */
  questionnaireId?: string;
  /** Url of the Questionnaire from FHIR */
  questionnaireUrl?: string;
  /** Id of the QuestionnaireResponse from FHIR */
  questionnaireResponseId?: string;
  /** if request done by a physician**/
  isPhysician?: boolean;
  /**
   * If specified, the questionnaire will be rendered to the provided component.
   *
   * The component can be specified as a class or an id of an HTML element.
   * To specify a class, prepend the class name with a period ('.').
   * To specify an id, prepend the id with a hash ('#').
   *
   * Example: '.questionnaire-container' or '#questionnaire'
   */
  renderOptions?: {
    renderTo?: string;
  };
};

/**
 * Represents parameters for submitting a questionnaire.
 * Most likely for our purposes  author = source = subject
 */
export type QuestionnaireSubmitParam = Pick<
  QuestionnaireFormInstance,
  /**
   * The subject of the questionnaire/
   * This could be a patient, organization, practitioner, device, etc.
   * This is who/what the answers apply to, but is not necessarily the source of information.
   */
  | 'subject'
  /**
   * The source of the questionnaire.
   * This could be the entity that provided the questionnaire.
   * It might be the same as the subject or different.
   */
  | 'source'
  /**
   * The author of the questionnaire.
   * The individual or device that received the answers to the questions in the QuestionnaireResponse and recorded them in the system.
   */
  | 'author'
  /**
   *  Whether or not FHIR resources should be extracted from the QuestionnaireResponse
   *
   */
  | 'extractionEnabled'
  | 'metaInformation'
  /**
   *  A status of QuestionnaireResponse. FE can send a status of QuestionnaireResponse on which depends whether we are able to edit QuestionnaireResponse
   *  after save or not.
   *
   */
  | 'status'
>;

export enum QuestionnaireSubmitResponseMessage {
  SUCCESS,
  NO_SUBMIT_PARAMS,
  QUESTIONNAIRE_HAS_REQUIRED_FIELDS,
  QUESTIONNAIRE_EMPTY,
  ERROR_FROM_SERVER
}

export type QuestionnaireSubmitResponseParam = {
  success: boolean;
  message: QuestionnaireSubmitResponseMessage;
  questionnaireResponseId: string | undefined;
  active: boolean;
};

export type QuestionnaireViewResponseParam = {
  success: boolean;
  active: boolean;
};

export interface DownloadDocumentParam {
  documentDataAccessorKey: string;
  format?: 'bulk' | 'converted' | 'raw';
}

export type ImportPluginConfigParam = {
  isDicomPluginNone: boolean;
  isDocumentPluginNone: boolean;
};

export type PatientTaskParam = {
  task: icModel.saf.infrastructure.model.appointments.Task;
  appointmentId: string;
  isPhysician?: boolean;
};

export type PatientTaskResponseParam = {
  taskIsCompleted: boolean;
};

export type BookAppointmentParam = {
  resource?: string;
  service?: GenCode;
  speciality?: GenCode;
  areaId?: string;
  renderToElement?: string;
  hideTitle: boolean;
  hideCloseButton: boolean;
  isBookTask: boolean;
};

export type BookingAppointmentResponseParam = {
  identifier?: string;
};

export type RescheduleAppointmentParam = {
  resource: HealthCareProvider | undefined;
  center: HealthCareProvider | undefined;
  service: GenCode | undefined;
  coveragePlan: GenCode | undefined;
  id: string;
};

export type RescheduleAppointmentResponseParam = {
  appointmentId: string;
};

export type RedirectToAppointmentParam = {
  appointmentIdentifier: string;
};

export type ExternalAppEmbeddingParam = {
  title: string;
  renderTo: {
    id: string;
  };
};
// ^-- ADD NEW EventParam TYPES

/**
 * Mapping from event name to parameter type.
 * When receiving or sending new events in react, please add typing here for safety and convenience :)
 *
 */
export interface PoeEventParameterTypes extends Record<string, unknown> {
  /** Fired by the POE on first load */
  init: void;
  /** Fired to open a document in one of the content/dicom viewers */
  displayDocument: DisplayDocumentEventParam;
  /** Fired when a document is closed from the content/dicom viewer (through it's own panel header) */
  closeDocument: Pick<DisplayDocumentEventParam, 'documentDataAccessorKey'>;
  /** Fired when all viewers which are rendered to passed div (renderTo) should be closed */
  closeDocumentByRenderTo: string | undefined;
  /** Fired when all viewers (content viewer and dicom viewer) need to be closed. This happens for example when switching patient. */
  closeAllViewers: void;
  /** Fired when an emergency access was activated for the current patient context */
  activatedEmergencyAccess: void;
  /** pre-fill a search filter for the PatientSearchPortlet. */
  setPatientFilter: SetPatientFilterEventParam;
  /** remove a patient from the selection. */
  removePatientFromScope: string;
  /** deselect the current patient. */
  removeCurrentPatientFromScope: void;
  /** Fired by the SenseFormPortlet when deleting a document from teleconsultation. */
  docRemovedTeleconsultation: DocRemovedTeleconsultationEventParam;
  /** Fired by the HPDFindSelectPortlet on selection. */
  HCProviderSelected: HCProviderSelectedEventParam;
  /** Fired by some old portlets. TODO remove! */
  uploadFilesWindowCreated: boolean;
  /** Fired by the DocumentUploadPortlet after a successful upload. */
  refreshDocumentQueryResult: RefreshDocumentQueryResultEventParam;
  /** Trigger an upload. */
  uploadFiles: void;
  /** Trigger an update upload. Document ID should be in Data accessor as documentInformationToUpdate. */
  updatedocument: void;
  /** Used to trigger the DocumentUploadPortlet in import mode. */
  importFilesFromDataAccessor: void;
  /** Fired by the RecordImportPortlet. */
  RIPImportStateEvent: RIPImportStateEventParam;
  /** Consumable by RecordImportPortlet. */
  downloadDICOMRequestCall: RecordImportEventParam;
  importRequestCall: RecordImportEventParam;
  batchImportCall: BatchRecordImportEventParam;
  /** To trigger the PatientCreateEditPortlet to be shown for creating a new patient. */
  createNewPatient: CreateStartEventParam;
  /** To trigger the PatientCreateEditPortlet to be shown for editing a patient. */
  editPatient: EditStartEventParam;
  /** Fired by the PatientCreateEditPortlet when a patient has been created/updated. */
  patientSaved: void;
  /** Fired by the PatientWizardPortlet when a patient was selected for creating/editing through the wizard. */
  wizardSelection: PatientWizardSelectionParam;
  /** Fired by the PatientSearchPortlet to inform other portlets of the configured default zpiSearch value. */
  informOfZpiSearchDefaultConfig: boolean;
  /** Fired by the PatientSearchPortlet to inform other portlets of zpiSearch filter value. */
  zpiSearchFilterChanged: boolean;
  /** Fired by the EmergencyCaseManagementPortlet to inform other portlets of the document render options. */
  setDocumentRenderOptions: DocumentRenderOptionsParam;
  /** Fired by the PhysicianSearchPortlet and DashboardPortlet to instruct HeathcareProviderDetailsPortlet to open modal with HP details.  */
  openHealthcareProviderDetails: OpenHealthcareProviderDetailsParam;
  /** Fired by Portlets to force download document file. */
  downloadDocumentByContentViewer: DownloadDocumentParam;
  /** To trigger Questionnaire Portlet to render questionnaire / to be shown */
  getQuestionnaire: QuestionnaireParam;
  /** Fired by Questionnaire Portlet as a response for questionnaire rendering */
  questionnaireViewResponse: QuestionnaireViewResponseParam;
  /** To trigger Questionnaire Portlet to submit the questionnaire */
  submitQuestionnaire: QuestionnaireSubmitParam;
  /** Fired by Questionnaire Portlet as a response for questionnaire submitting*/
  questionnaireSubmitResponse: QuestionnaireSubmitResponseParam;
  /** Fired by RecordImportPorlet to let HealthRecordPortlet know if it should display import button */
  importPluginConfig: ImportPluginConfigParam;
  /** Fired by AppointmentSchedulingPortlet and DashboardPortlet to instruct TaskPortlet to open modal with Task*/
  openTask: PatientTaskParam;
  /** Fired by TaskPortlet as a response for open Task request*/
  openTaskResponse: PatientTaskResponseParam;
  /** Fired by AppointmentBookingPortlet to instruct TaskPortlet to close modal with TaskDetail*/
  closeTask: void;
  /** Fired by AppointmentSchedulingPortlet and DashboardPortlet to instruct AppointmentBookingPortlet to open booking modal*/
  bookingAppointment: void;
  /** Fired by AppointmentBookingPortlet AppointmentSchedulingPortlet to refresh upcoming appointments*/
  bookingAppointmentResponse: BookingAppointmentResponseParam;
  /** Fired by AppointmentSchedulingPortlet to instruct AppointmentBookingPortlet to open booking modal with filter parameters*/
  bookAppointmentWithParameters: BookAppointmentParam;
  /** Fired by AppointmentSchedulingPortlet to instruct AppointmentBookingPortlet to open booking modal with filter parameters*/
  rescheduleAppointment: RescheduleAppointmentParam;
  /** Fired by AppointmentSchedulingPortlet to instruct AppointmentBookingPortlet to open booking modal with filter parameters*/
  rescheduleAppointmentResponse: RescheduleAppointmentResponseParam;
  /** Fired by TaskPortlet to trigger redirect functionality of Dashboard and Appointment Scheudling portlet to given appointment  */
  redirectToAppointment: RedirectToAppointmentParam;
  /** Fired by Patient Chart Portlet to ask External App embedding to render the content to the widget */
  renderExternalApp: ExternalAppEmbeddingParam;
  // ^-- ADD YOUR Event -> EventParam MAPPINGS HERE
}
